import React from 'react'
import PropTypes from 'prop-types'

import ProgressBar from './ProgressBar'
import CancelUploadingModal from './CancelUploadingModal'

class UploadProcess extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      uploadedModelsCount: 0,
      retriesLeft: 50,
      uploadFailedDisplayed: false,
      models: this.props.modelsToUpload.map(model => {
        return {
          ...model,
          filesLeftToUpload: model.images.length + model.files.length,
          fileKeys: [],
          imageKeys: [],
          isUploaded: false,
        }
      }),
      cancelModalIsOpen: false,
      modalText: '',
      targetLocation: '',
    }

    this.onUnload = this.onUnload.bind(this)
    this.uploadToS3 = this.uploadToS3.bind(this)
    this.onS3FileUpload = this.onS3FileUpload.bind(this)
    this.createItem = this.createItem.bind(this)
    this.onModelFolderUploadSuccess = this.onModelFolderUploadSuccess.bind(this)
    this.closeModal = this.closeModal.bind(this)
    this.showCancelModal = this.showCancelModal.bind(this)
    this.terminateUploading = this.terminateUploading.bind(this)
    this.handleLinkClick = this.handleLinkClick.bind(this)
  }

  componentDidMount() {
    this.cancelSource = axios.CancelToken.source()
    this.uploadNextModel()
    window.addEventListener('beforeunload', this.onUnload)
    window.addEventListener('click', this.handleLinkClick)
  }

  handleLinkClick(e) {
    if (['A', 'IMG', 'SPAN', 'svg', 'path', 'DIV'].includes(e.target.tagName)) {
      if (e.target.getAttribute('id') === 'upload-models') {
        return
      }

      if (e.target.getAttribute('target') === '_blank') {
        return
      }

      if (['SPAN', 'svg', 'DIV'].includes(e.target.tagName) && e.target.parentElement.tagName !== 'A') {
        return
      }

      if (e.target.tagName === 'path' && e.target.parentElement.parentElement.tagName !== 'A') {
        return
      }

      window.removeEventListener('beforeunload', this.onUnload)
      e.preventDefault()
      this.setState({ targetLocation: e.target.closest('a').getAttribute('href') })
      this.showCancelModal('Exiting this page will terminate the upload!')
    }
  }

  showCancelModal(content) {
    this.setState({ cancelModalIsOpen: true, modalText: content })
  }

  closeModal() {
    window.addEventListener('beforeunload', this.onUnload)
    this.setState({ cancelModalIsOpen: false, targetLocation: null })
  }

  terminateUploading() {
    window.removeEventListener('beforeunload', this.onUnload)
    window.removeEventListener('click', this.handleLinkClick)
    this.props.onUploadProcessCancel()

    if (this.state.targetLocation) {
      window.location.href = this.state.targetLocation
    } else {
      this.props.onUploadProcessCancel()
    }
  }

  uploadNextModel() {
    const modelToUpload = this.state.models.find(model => !model.isUploaded)

    if (modelToUpload) {
      modelToUpload.images.forEach(image => this.fetchModelFileStorageLocation(modelToUpload, image))
      modelToUpload.files.forEach(file => this.fetchModelFileStorageLocation(modelToUpload, file))
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.onUnload)
    window.removeEventListener('click', this.handleLinkClick)
  }

  retryUpload(model, file) {
    if (this.state.retriesLeft > 0) {
      this.setState({ retriesLeft: this.state.retriesLeft - 1 })

      this.fetchModelFileStorageLocation(model, file)
    } else if (!this.state.uploadFailedDisplayed) {
      this.setState({ uploadFailedDisplayed: true })

      alert('Upload failed too many times, please contact our support.')
    }
  }

  onUnload(event) {
    const closingTabWarningText = 'Upload in progress. Are you sure to close?'
    event.returnValue = closingTabWarningText
    return closingTabWarningText
  }

  fetchModelFileStorageLocation(model, file) {
    axios.post(`/api/internal/batch-uploads/${this.props.batchUploadId}/asset-tokens`, { cancelToken: this.cancelSource.token })
      .then(response => this.uploadToS3(model, file, response.data.data))
      .catch(() => this.retryUpload(model, file))
  }

  uploadToS3(model, file, token) {
    const escapedFileName = file.name.replace(/#/g, '_')
    const fileKey = `${token.storage.key}`.replace('${filename}', escapedFileName)
    const bodyFormData = new FormData()
    file.key = fileKey

    Object.keys(token.storage).map((storageKey) => {
      bodyFormData.set(storageKey, token.storage[storageKey])
    })
    bodyFormData.set('key', fileKey)
    bodyFormData.set('file', file)

    delete axios.defaults.headers.common['X-Requested-With']
    axios.post(
      token.storageLocation,
      bodyFormData,
      { headers: { 'Content-Type': 'multipart/form-data' }, cancelToken: this.cancelSource.token }
    )
      .then(() => this.onS3FileUpload(model, file))
      .catch(() => this.retryUpload(model, file))
    axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
  }

  onS3FileUpload(model, file) {
    model.filesLeftToUpload -= 1

    if (file.type.includes('image')) {
      model.imageKeys.push(file.key)
    } else {
      model.fileKeys.push(file.key)
    }

    if (model.filesLeftToUpload === 0) {
      model.isUploaded = true
      this.createItem(model.folderName, model.imageKeys, model.fileKeys)
      this.uploadNextModel()
    }
  }

  createItem(title, images, files) {
    axios.post(`/api/internal/batch-uploads/${this.props.batchUploadId}/items`, {
      data: {
        attributes: {
          title,
          images,
          files,
        },
      },
    }, { cancelToken: this.cancelSource.token })
      .then(response => this.onModelFolderUploadSuccess(response))
      .catch(response => {
        console.error('Item creation failed')
        console.error(response)
      })
  }

  completeBatch() {
    axios.patch(`/api/internal/batch-uploads/${this.props.batchUploadId}`, {
      data: { attributes: { status: 'completed' } },
    }, { cancelToken: this.cancelSource.token })
      .then(response => {
        window.removeEventListener('beforeunload', this.onUnload)
        window.removeEventListener('click', this.handleLinkClick)
        this.props.onUploadProcessComplete()
      })
      .catch(response => {
        console.error('Batch update failed')
        console.error(response)
      })
  }

  onModelFolderUploadSuccess(response) {
    this.props.onModelUploadComplete(response.data.data)

    this.setState({ uploadedModelsCount: this.state.uploadedModelsCount + 1 })

    if (this.state.uploadedModelsCount === this.state.models.length) {
      this.completeBatch()
    }
  }

  render() {
    return (
      <span>
        <CancelUploadingModal
          modalIsOpen={this.state.cancelModalIsOpen}
          modalText={this.state.modalText}
          closeModal={this.closeModal}
          terminateUploading={this.terminateUploading}
        />

        <ProgressBar
          progress={this.state.uploadedModelsCount}
          total={this.state.models.length}
          onCancel={this.showCancelModal}
        />
      </span>
    )
  }
}

UploadProcess.propTypes = {
  batchUploadId: PropTypes.string.isRequired,
  modelsToUpload: PropTypes.array.isRequired,
  onModelUploadComplete: PropTypes.func,
  onUploadProcessComplete: PropTypes.func,
}

export default UploadProcess
